import { getLayerNameByElemType, parseInt } from 'common/helpers';
import {
  ElemTypes,
  IChangeGroupStyles,
  IElemCoords,
  IElement,
  IElemsGroupMainStyles,
  IGuide,
  IStickyPoints,
  IStylesByElemId,
  MovementDirectionType,
  TypeChangeStylesHandler,
} from 'common/interfaces';
import Group from 'components/Group';
import { CSSProperties } from 'react';
import { IGroupAgainItem } from 'store/reducers/elems.slice';
import { v4 } from 'uuid';

import CollectionService from './CollectionService';
import CoordsService from './CoordsService';

class ElemsService {
  checkGroupElem(elem: IElement): boolean {
    return Boolean(elem.elemType === ElemTypes.GROUP && elem.children);
  }

  checkChildElem(elem: IElement): boolean {
    return Boolean(elem.groupId);
  }

  getGroupLimitValues<T extends { styles: CSSProperties }>(
    objWithStyles: T[],
  ): IElemsGroupMainStyles {
    const offsetLeftValues: number[] = [];
    const offsetTopValues: number[] = [];
    const offsetRightValues: number[] = [];
    const offsetBottomValues: number[] = [];

    for (const item of objWithStyles) {
      const leftValue = parseInt(item.styles.left as string);
      offsetLeftValues.push(leftValue);

      const topValue = parseInt(item.styles.top as string);
      offsetTopValues.push(topValue);

      const widthValue = parseInt(item.styles.width as string);
      const heightValue = parseInt(item.styles.height as string);

      const rightValue = leftValue + widthValue;
      offsetRightValues.push(rightValue);

      const bottomValue = topValue + heightValue;
      offsetBottomValues.push(bottomValue);
    }

    const minLeftValue = Math.min(...offsetLeftValues);
    const minTopValue = Math.min(...offsetTopValues);

    const maxRightValue = Math.max(...offsetRightValues);
    const maxBottomValue = Math.max(...offsetBottomValues);

    const widthValue = maxRightValue - minLeftValue;
    const heightValue = maxBottomValue - minTopValue;

    return {
      left: minLeftValue,
      top: minTopValue,
      width: widthValue,
      height: heightValue,
    };
  }

  getGroupChildren(
    elems: IElement[],
    groupElemId: string,
    groupLimitValues: IElemsGroupMainStyles,
  ): IElement[] {
    return elems.map((elem) => {
      const topValue =
        parseInt(elem.styles.top as string) - groupLimitValues.top;
      const leftValue =
        parseInt(elem.styles.left as string) - groupLimitValues.left;

      const transformedElem: IElement = {
        ...elem,
        groupId: groupElemId,
        styles: { ...elem.styles, top: topValue, left: leftValue },
      };
      return transformedElem;
    });
  }

  ungroupGroupChildren(
    groupElem: IElement,
    extractableСhildren: IElement[],
    removeGroupId?: boolean,
  ): IElement[] {
    const groupLeftOffset = parseInt(groupElem.styles.left as string);
    const groupTopOffset = parseInt(groupElem.styles.top as string);

    return extractableСhildren.map((item) => {
      const preparedElem: IElement = {
        ...item,
        groupId: removeGroupId ? null : item.groupId,
        styles: {
          ...item.styles,
          left: groupLeftOffset + parseInt(String(item.styles.left)) + 'px',
          top: groupTopOffset + parseInt(String(item.styles.top)) + 'px',
        },
      };

      return preparedElem;
    });
  }

  replaceGroupChildren(
    elemsList: IElement[],
    groupElemId: string,
    groupChildren: IElement[],
  ): IElement[] {
    const groupPositionAndSizeStyles = this.getGroupLimitValues(groupChildren);

    const updatedChildren = this.getGroupChildren(
      groupChildren,
      groupElemId,
      groupPositionAndSizeStyles,
    );

    const result = elemsList.map((item) => {
      if (item.id === groupElemId) {
        const preparedElem: IElement = {
          ...item,
          styles: { ...item.styles, ...groupPositionAndSizeStyles },
        };
        return preparedElem;
      }

      const changedChild = updatedChildren.find((i) => i.id === item.id);

      if (changedChild) return changedChild;

      return item;
    });

    for (const item of updatedChildren) {
      if (!result.find((i) => i.id === item.id)) {
        result.push(item);
      }
    }

    return result;
  }

  prepareElemsForInsertion(elems: IElement[]): IElement[] {
    return elems.map((elem) => {
      const elemId = v4();

      const preparedElem: IElement = {
        ...elem,
        id: elemId,
        elem: <elem.elem.type id={elemId} />,
        styles: {
          ...elem.styles,
          left: parseInt(elem.styles.left as string) + 10 + 'px',
          top: parseInt(elem.styles.top as string) + 10 + 'px',
        },
      };

      return preparedElem;
    });
  }

  insertDraggableElemCopy(
    draggableElem: IElement,
    initialElems: IElement[],
  ): IElement[] {
    const elemIndex = initialElems.indexOf(draggableElem);

    const elemId = v4();

    const preparedElemCopy: IElement = {
      ...draggableElem,
      id: elemId,
      elem: <draggableElem.elem.type id={elemId} />,
      children: draggableElem.children
        ? draggableElem.children.map((childElem) => {
            const childId = v4();

            const preparedChild: IElement = {
              ...childElem,
              id: childId,
              elem: <childElem.elem.type id={childId} />,
              groupId: elemId,
            };

            return preparedChild;
          })
        : null,
    };

    // TODO: переделать на deep clone из сервиса
    const newElems = [...initialElems];

    newElems.splice(elemIndex, 0, preparedElemCopy);

    return newElems;
  }

  updateElemsWhenResizeSelection(
    initialElems: IElement[],
    stylesByElemId: IStylesByElemId[],
  ): IElement[] {
    return initialElems.map((elem) => {
      const elemInSelected = stylesByElemId.find((item) => item.id === elem.id);

      if (elemInSelected) {
        // если есть дети
        if (elem.elemType === ElemTypes.GROUP && elemInSelected.children) {
          const groupChildren = elem.children
            ? elem.children.map((item) => {
                const newChildStyles = elemInSelected.children?.find(
                  (child) => child.id === item.id,
                )?.styles;

                if (newChildStyles) {
                  const newItem: IElement = {
                    ...item,
                    styles: { ...item.styles, ...newChildStyles },
                  };

                  return newItem;
                }

                return item;
              })
            : null;

          const newGroupElem: IElement = {
            ...elem,
            styles: { ...elem.styles, ...elemInSelected.styles },
            children: groupChildren,
          };

          return newGroupElem;
        } else {
          // если нет детей
          const changedElem: IElement = {
            ...elem,
            styles: { ...elem.styles, ...elemInSelected.styles },
          };
          return changedElem;
        }
      } else {
        return elem;
      }
    });
  }

  changeGroupElemsStyles(
    initialElems: IElement[],
    updatedGroupStyles: IChangeGroupStyles,
  ): IElement[] {
    return initialElems.map((elem) => {
      if (
        elem.elemType === ElemTypes.GROUP &&
        elem.id === updatedGroupStyles.id
      ) {
        const groupChildren = elem.children
          ? elem.children.map((item) => {
              const newChildStyles = updatedGroupStyles.children?.find(
                (child) => child.id === item.id,
              )?.styles;

              if (newChildStyles) {
                const newItem: IElement = {
                  ...item,
                  styles: { ...item.styles, ...newChildStyles },
                };

                return newItem;
              }

              return item;
            })
          : null;

        const newGroupElem: IElement = {
          ...elem,
          styles: { ...elem.styles, ...updatedGroupStyles.styles },
          children: groupChildren,
        };

        return newGroupElem;
      } else {
        return elem;
      }
    });
  }

  ungroupGroupElemChildren(groupElems: IElement[]): IElement[] {
    const ungroupedChildren: IElement[] = [];

    for (const groupElem of groupElems) {
      const leftValue = parseInt(groupElem.styles.left as string);
      const topValue = parseInt(groupElem.styles.top as string);

      if (groupElem.children) {
        for (const childElem of groupElem.children) {
          const preparedChildElem: IElement = {
            ...childElem,
            groupId: null,
            styles: {
              ...childElem.styles,
              left:
                leftValue + parseInt(childElem.styles.left as string) + 'px',
              top: topValue + parseInt(childElem.styles.top as string) + 'px',
            },
          };

          ungroupedChildren.push(preparedChildElem);
        }
      }
    }

    return ungroupedChildren;
  }

  extractElemChildrenIfNeeded(elems: IElement[]): IElement[] {
    let allElems: IElement[] = [];

    for (const elem of elems) {
      if (elem.elemType === ElemTypes.GROUP && elem.children) {
        const preparedChildren = this.ungroupGroupElemChildren([elem]);

        allElems = [...allElems, ...preparedChildren];
      } else {
        allElems.push(elem);
      }
    }

    return allElems;
  }

  groupElemsByIds(
    initialElems: IElement[],
    elemsIds: string[],
    customGroupId?: string,
  ): { groupElemId: string; updatedElems: IElement[] } {
    const groupableElems = initialElems.filter((elem) =>
      elemsIds.includes(elem.id),
    );
    const updatedElems = initialElems.filter(
      (elem) => !elemsIds.includes(elem.id),
    );

    const groupLimitValues = this.getGroupLimitValues(groupableElems);

    const groupInitialStyles: React.CSSProperties = {
      ...groupLimitValues,
      position: 'absolute',
    };

    const groupElemId = customGroupId || v4();

    const preparedGroupableElems =
      this.extractElemChildrenIfNeeded(groupableElems);

    const groupChildren = this.getGroupChildren(
      preparedGroupableElems,
      groupElemId,
      groupLimitValues,
    );

    const groupElem: IElement = {
      id: groupElemId,
      elem: <Group id={groupElemId} />,
      styles: groupInitialStyles,
      elemType: ElemTypes.GROUP,
      children: groupChildren,
      groupId: null,
      layerName: getLayerNameByElemType(ElemTypes.GROUP),
      isHidden: false,
      isBlocked: false,
      showNestedLayers: true,
    };

    const groupIndex = initialElems.indexOf(groupableElems[0]);

    updatedElems.splice(groupIndex, 0, groupElem);

    return { groupElemId, updatedElems };
  }

  ungroupElemsByIds(
    initialElems: IElement[],
    selectedElemsIds: string[],
    elemsIds: string[],
  ): { newSelectedElemsIds: string[]; updatedElems: IElement[] } {
    let updatedElems: IElement[] = [];
    const ungroupedChildrenIds: string[] = [];
    const groupElemsIds: string[] = [];

    for (const elem of initialElems) {
      if (
        elemsIds.includes(elem.id) &&
        elem.elemType === ElemTypes.GROUP &&
        elem.children
      ) {
        const ungroupedChildren: IElement[] = [];

        const leftValue = parseInt(elem.styles.left as string);
        const topValue = parseInt(elem.styles.top as string);

        for (const childElem of elem.children) {
          const preparedChildElem: IElement = {
            ...childElem,
            groupId: null,
            styles: {
              ...childElem.styles,
              left:
                leftValue + parseInt(childElem.styles.left as string) + 'px',
              top: topValue + parseInt(childElem.styles.top as string) + 'px',
            },
          };

          ungroupedChildren.push(preparedChildElem);
          ungroupedChildrenIds.push(childElem.id);
        }

        updatedElems = [...updatedElems, ...ungroupedChildren];
        groupElemsIds.push(elem.id);
      } else {
        updatedElems.push(elem);
      }
    }

    const newSelectedElemsIds = [
      ...selectedElemsIds.filter((id) => !groupElemsIds.includes(id)),
      ...ungroupedChildrenIds,
    ];

    return { newSelectedElemsIds, updatedElems };
  }

  getSelectableElemsIds(areaElem: HTMLDivElement, elems: IElement[]): string[] {
    const areaCoords = CoordsService.getCoords(areaElem);

    const selectableElems: string[] = [];

    for (const elem of elems) {
      if (elem.isBlocked || elem.isHidden) continue;

      const elemCoords: IElemCoords = {
        left: parseInt(elem.styles.left as string),
        top: parseInt(elem.styles.top as string),
        right:
          parseInt(elem.styles.left as string) +
          parseInt(elem.styles.width as string),
        bottom:
          parseInt(elem.styles.top as string) +
          parseInt(elem.styles.height as string),
      };

      if (
        elemCoords.left > areaCoords.right ||
        elemCoords.right < areaCoords.left ||
        elemCoords.bottom < areaCoords.top ||
        elemCoords.top > areaCoords.bottom
      ) {
        continue;
      }

      selectableElems.push(elem.id);
    }

    return selectableElems;
  }

  updateGroupElemByNewChildren(
    groupElem: IElement,
    newGroupChildren: IElement[],
  ): IElement {
    const ungroupedChildren = this.ungroupGroupChildren(
      groupElem,
      newGroupChildren,
    );

    const groupPositionAndSizeStyles =
      this.getGroupLimitValues(ungroupedChildren);

    const updatedChildren = this.getGroupChildren(
      ungroupedChildren,
      groupElem.id,
      groupPositionAndSizeStyles,
    );

    return {
      ...groupElem,
      styles: { ...groupElem.styles, ...groupPositionAndSizeStyles },
      children: updatedChildren,
    };
  }

  findElemsAndChangeStylesWithHandler(
    elems: IElement[],
    ids: string[],
    handler: TypeChangeStylesHandler,
  ): IElement[] {
    return CollectionService.findByIdsAndUpdateWithHandler<IElement>(
      elems,
      ids,
      {
        styles: (value) => handler(value),
      },
    );
  }

  addSelectedChildElem(
    initialElems: IElement[],
    selectedElemsIds: string[],
    needToGroupAgainCollection: IGroupAgainItem[],
    childElem: IElement,
  ): {
    newSelectedElemsIds: string[];
    updatedElems: IElement[];
    needToGroupAgain: IGroupAgainItem[];
  } | null {
    if (!childElem.groupId) return null;

    const { updatedElems } = this.ungroupElemsByIds(
      initialElems,
      selectedElemsIds,
      [childElem.groupId],
    );

    const groupElem = CollectionService.findElemById<IElement>(
      initialElems,
      childElem.groupId,
    );

    let needToGroupAgain = [...needToGroupAgainCollection];

    if (groupElem?.children) {
      const groupChildrenIds = groupElem?.children.map((item) => item.id);

      needToGroupAgain = [
        ...needToGroupAgain,
        { groupId: childElem.groupId, childrenIds: groupChildrenIds },
      ];
    }

    const newSelectedElemsIds = [
      ...selectedElemsIds.filter((item) => item !== childElem.groupId),
      childElem.id,
    ];

    return { newSelectedElemsIds, updatedElems, needToGroupAgain };
  }

  setSelectedChildElem(
    initialElems: IElement[],
    childElem: IElement,
    selectedElemsIds: string[],
    isLayersRenderDisabled: boolean,
  ): {
    updatedElems: IElement[] | null;
    needToGroupAgain: IGroupAgainItem[] | null;
    updatedSelectedElemsIds: string[] | null;
  } | null {
    if (!childElem.groupId) return null;

    let updatedSelectedElemsIds = [...selectedElemsIds];

    if (isLayersRenderDisabled) {
      updatedSelectedElemsIds = [childElem.id];

      return {
        updatedElems: null,
        needToGroupAgain: null,
        updatedSelectedElemsIds,
      };
    } else {
      const { updatedElems } = this.ungroupElemsByIds(
        initialElems,
        selectedElemsIds,
        [childElem.groupId],
      );

      const groupElem = CollectionService.findElemById<IElement>(
        initialElems,
        childElem.groupId,
      );

      if (!groupElem?.children) return null;

      const groupChildrenIds = groupElem?.children.map((item) => item.id);

      const needToGroupAgain: IGroupAgainItem[] = [
        { groupId: childElem.groupId, childrenIds: groupChildrenIds },
      ];

      return { updatedElems, needToGroupAgain, updatedSelectedElemsIds: null };
    }
  }

  groupElemsWhenChildElemTransformStop(
    initialElems: IElement[],
    needToGroupAgainCollection: IGroupAgainItem[],
  ): { newElems: IElement[]; groupIds: string[] } {
    let newElems = [...initialElems];
    const groupIds: string[] = [];

    for (const item of needToGroupAgainCollection) {
      if (item.childrenIds.length <= 1) continue;

      const { updatedElems } = this.groupElemsByIds(
        newElems,
        item.childrenIds,
        item.groupId,
      );

      groupIds.push(item.groupId);
      newElems = updatedElems;
    }

    return { newElems, groupIds };
  }

  getElemsStickyPoints(elems: IElement[], guides: IGuide[]): IStickyPoints[] {
    let stickyPoints = elems
      .filter((item) => !item.isBlocked && !item.isHidden)
      .map((elem) => {
        const leftX = parseInt(elem.styles.left);
        const centerX = leftX + parseInt(elem.styles.width) / 2;
        const rightX = leftX + parseInt(elem.styles.width);

        const topY = parseInt(elem.styles.top);
        const centerY = topY + parseInt(elem.styles.height) / 2;
        const bottomY = topY + parseInt(elem.styles.height);

        const points: IStickyPoints = {
          id: elem.id,
          xPoints: [leftX, centerX, rightX],
          yPoints: [topY, centerY, bottomY],
        };

        return points;
      });

    if (guides.length) {
      const guidesPoints: IStickyPoints[] = guides
        .filter((item) => !item.isBlocked)
        .map((guide): IStickyPoints => {
          return {
            id: guide.id,
            xPoints: [guide.x],
            yPoints: [guide.y],
          };
        });
      stickyPoints = [...stickyPoints, ...guidesPoints];
    }

    return stickyPoints;
  }

  checkIsBlockElemsSelection(selectedElems: IElement[]): boolean {
    let isBlocked = false;

    for (const item of selectedElems) {
      if (item.isBlocked) {
        isBlocked = true;
        break;
      }
    }

    return isBlocked;
  }

  getChangedStylesWhenArrowKeyDown(
    styles: CSSProperties,
    direction: MovementDirectionType,
    withShiftKey: boolean,
  ): CSSProperties {
    const offset = withShiftKey ? 10 : 1;

    let newProperties: CSSProperties = {};

    switch (direction) {
      case 'top':
        newProperties = { top: parseInt(styles.top) - offset + 'px' };
        break;
      case 'right':
        newProperties = { left: parseInt(styles.left) + offset + 'px' };
        break;
      case 'bottom':
        newProperties = { top: parseInt(styles.top) + offset + 'px' };
        break;
      case 'left':
        newProperties = { left: parseInt(styles.left) - offset + 'px' };
        break;
      default:
        break;
    }

    return {
      ...styles,
      ...newProperties,
    };
  }

  moveSelectedElemsOnKeyDown(
    elems: IElement[],
    ids: string[],
    direction: MovementDirectionType,
    withShiftKey: boolean,
  ): IElement[] {
    return CollectionService.findByIdsAndUpdateWithHandler<IElement>(
      elems,
      ids,
      {
        styles: (styles) =>
          this.getChangedStylesWhenArrowKeyDown(
            styles,
            direction,
            withShiftKey,
          ),
      },
    );
  }
}

export default new ElemsService();
